import React, { type ReactNode, useLayoutEffect, useRef } from 'react'

const MIN_FRAME_HEIGHT_PX = 1

type ThreadPanelActivityBoundaryProps = {
    active: boolean
    children: ReactNode
}

export default function ThreadPanelActivityBoundary({
    active,
    children,
}: ThreadPanelActivityBoundaryProps) {
    const frameRef = useRef<HTMLDivElement | null>(null)
    const reservedHeightRef = useRef(MIN_FRAME_HEIGHT_PX)

    useLayoutEffect(() => {
        if (!active) return
        const frame = frameRef.current
        if (!frame) return

        const measure = () => {
            const nextHeight = frame.getBoundingClientRect().height
            if (!Number.isFinite(nextHeight) || nextHeight <= 0) return
            // The next parent-driven active -> inactive render consumes this value. A ref
            // avoids rerendering the boundary whenever distant segment geometry settles.
            reservedHeightRef.current = nextHeight
        }

        measure()
        if (typeof ResizeObserver === 'undefined') return
        const observer = new ResizeObserver(measure)
        observer.observe(frame)
        return () => observer.disconnect()
    }, [active])

    const reservedHeight = reservedHeightRef.current

    return (
        <div
            ref={frameRef}
            aria-hidden={active ? undefined : true}
            data-thread-panel-content-active={active ? 'true' : 'false'}
            data-thread-panel-inactive-spacer={active ? undefined : 'true'}
            data-thread-panel-reserved-height={Math.round(reservedHeight)}
            style={active ? undefined : { height: reservedHeight, overflow: 'hidden' }}
        >
            {active ? children : null}
        </div>
    )
}
